import { ChangeEvent, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Slider from 'react-rangeslider'
import 'react-rangeslider/lib/index.css'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { setBalance } from '../../app/reducers/balance/balance.slice'
import { setError } from '../../app/reducers/error/error.slice'
import { setCurrentModal } from '../../app/reducers/modal/modal.slice'
import { ModalsTypesEnum } from '../../app/reducers/modal/modal.types'
import { useApiContext } from '../../contexts/ApiContext'
import ColorCard from '../SAIRUS_NEW/ColorCard/ColorCard'
import styles from './Calculator.module.scss'
import { CalculatorProps } from './Calculator.types'

export default function Calculator(props: CalculatorProps) {
	const [sliderValue, setSliderValue] = useState<number>(props.min)
	const [inputValue, setInputValue] = useState<string>(props.min.toString())
	const [timer, setTimer] = useState<ReturnType<typeof setTimeout>>()
	const { isAuth, user, response, updateUser } = useApiContext()
	const navigate = useNavigate()
	const { t } = useTranslation()
	const inputRef = useRef<HTMLInputElement>(null)

	const location = useLocation()

	const sliderHandler = (val: number) => {
		setSliderValue(val)
		setInputValue(val.toString())
	}

	const { balance } = useAppSelector(state => state.balance)

	const dispatch = useAppDispatch()

	const inputHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const rawValue = event.target.value
		setInputValue(rawValue)

		if (rawValue === '') {
			return
		}

		let parsedVal = Number(rawValue)

		if (isNaN(parsedVal)) {
			return
		}

		if (parsedVal > props.max) {
			parsedVal = props.max
			setInputValue(props.max.toString())
		}
		if (parsedVal >= props.min && parsedVal <= props.max) {
			setSliderValue(parsedVal)
			if (timer) clearTimeout(timer)
			setTimer(undefined)
		} else {
			if (parsedVal < props.min) {
				if (timer) clearTimeout(timer)
				setTimer(
					setTimeout(() => {
						endInput(parsedVal)
					}, 10000)
				)
			}
		}
	}

	const onBlurHandler = () => {
		if (inputValue === '' || Number(inputValue) < props.min) {
			setSliderValue(props.min)
			setInputValue(props.min.toString())
			if (inputRef.current) {
				inputRef.current.value = props.min.toString()
			}
		}
	}

	const endInput = (currentVal: number) => {
		if (currentVal < props.min) {
			setSliderValue(props.min)
			setInputValue(props.min.toString())
			if (inputRef.current) {
				inputRef.current.value = props.min.toString()
			}
		} else if (currentVal > props.max) {
			setSliderValue(props.max)
			setInputValue(props.max.toString())
			if (inputRef.current) {
				inputRef.current.value = props.max.toString()
			}
		} else {
			if (inputRef.current) {
				inputRef.current.value = currentVal.toString()
			}
		}
	}

	const save = async () => {
		if (!isAuth) {
			navigate('/login')
			return
		}
		if (!location.pathname.includes('/account/balance')) {
			navigate('/account/balance')
			return
		}

		if (
			user &&
			user.balances &&
			user.balances.length > 0 &&
			Number(user.balances[0].amount) >= sliderValue
		) {
			const data = {
				amount: sliderValue,
				type: props.type || 'MEGA',
			}
			const res = await response('/deposit/save', 'POST', JSON.stringify(data))

			if (res.code === 'OK') {
				dispatch(setBalance(balance - sliderValue))
				dispatch(setCurrentModal(ModalsTypesEnum.DEPOSIT_CREATED))
				await updateUser()
			} else {
				dispatch(setError(res.message))
			}
		} else dispatch(setCurrentModal(ModalsTypesEnum.REPLENISH))
	}

	const getProfitWrapperStyles = (color: string): string => {
		const result = [styles.profit_wrapper]
		switch (color) {
			case 'magenta':
				result.push(styles.profit_wrapper_magenta)
				break
			case 'purple':
				result.push(styles.profit_wrapper_purple)
				break
			case 'cian':
				result.push(styles.profit_wrapper_cian)
				break
			default:
				result.push(styles.profit_wrapper_magenta)
				break
		}
		return result.join(' ')
	}

	return (
		<ColorCard
			title={props.type}
			color={props.color}
			buttonLabel={t('calculator.invest') || ''}
			onClick={save}
		>
			<div className={styles.amountAndSum}>
				<div>
					<p className={styles.textS}>{t('calculator.income-per-day')}</p>
					<span className={styles.rate}>{props.rate.toFixed(1)}%</span>
				</div>
				<div>
					<p className={[styles.textS, styles.amount].join(' ')}>
						{t('calculator.amount-of-investment')}:
					</p>
					<div>
						<div className={styles.inputContainer}>
							<div className={styles.inputSmall}>
								<input
									type='number'
									className={styles.inputText}
									onChange={inputHandler}
									onBlur={onBlurHandler}
									value={inputValue}
									ref={inputRef}
									min={props.min}
									max={props.max}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className={styles.content}>
				<Slider
					value={sliderValue}
					min={props.min}
					max={props.max}
					onChange={sliderHandler}
					className={`swiper-no-swiping ${props.color}`}
				/>
				<div className={styles.limits}>
					<span className='text-xs'>
						{props.min} {t('currency')}
					</span>
					<span className='text-xs'>
						{props.max} {t('currency')}
					</span>
				</div>
				<div className={getProfitWrapperStyles(props.color)}>
					<div className={styles.profit}>
						<span>{t('calculator.profit-in-24-hours')}</span>
						<span>
							{(sliderValue * (props.rate / 100)).toFixed(1)} {t('currency')}
						</span>
					</div>
					<div className={styles.profit}>
						<span>{t('calculator.profit-per-week')}</span>
						<span>
							{(sliderValue * (props.rate / 100) * 7).toFixed(1)}{' '}
							{t('currency')}
						</span>
					</div>
					<div className={styles.profit}>
						<span>{t('calculator.profit-per-month')}</span>
						<span>
							{(sliderValue * (props.rate / 100) * 30).toFixed(1)}{' '}
							{t('currency')}
						</span>
					</div>
					<div className={styles.profit}>
						<span>{t('calculator.profit-per-year')}</span>
						<span>
							{(sliderValue * (props.rate / 100) * 356).toFixed(1)}{' '}
							{t('currency')}
						</span>
					</div>
				</div>
			</div>
		</ColorCard>
	)
}
